import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  AlloyButton,
  InfoIcon,
  Popup,
  SkeletonLoader,
  SuccessIcon,
  WarningIcon
} from 'cat-ecommerce-alloy';
import { actions } from './actions';
import styles from '../ProductFitment.module.scss';
import { useEffect, useState } from 'react';
import Conditional from '../../Conditional';
import FitmentContent from './FitmentContent';
import { FITMENT_CHECK } from '../../../../constants/commonConstants';
import cx from 'classnames';

const FitmentResult = ({
  variant,
  partNumber,
  didFitmentCallResolve,
  getFitmentResult
}) => {
  const [t] = useTranslation();
  const { FULL_FITMENT, PARTIAL_FITMENT, NO_FITMENT } = actions;
  const [option, setOption] = useState({});
  const { iconSize, classes, showPopup } = variant;
  const fitmentContentClassName = styles[classes];

  const model = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.model
  );
  const serialNumber = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.serialNumber
  );
  const equipmentFamily = useSelector(
    s => s.myEquipment.equipments?.selectedEquipment?.equipmentFamily
  );
  const fitmentResult = getFitmentResult(partNumber);
  const factoryFitText = t('PART_FACTORY_FIT');
  const partUnableToVerifyText = t('MEQ_FITMENT_COMPONENT_CTA4');

  const getfitmentLabel = (
    modelText,
    equipmentFamilyText,
    serialNumberText,
    isCard = false
  ) => {
    const modelLabel = modelText ? ` ${modelText}` : '';
    const serialNumberLabel = serialNumberText ? ` ${serialNumberText}` : '';
    const equipmentFamilyLabel = equipmentFamilyText
      ? ` ${equipmentFamilyText}`
      : '';

    if (isCard) {
      return `${factoryFitText}${modelLabel}${serialNumberLabel}`;
    }

    return `${modelLabel}${equipmentFamilyLabel}${serialNumberLabel}`;
  };

  const getButtonTitle = (
    isSuccess,
    modelText,
    equipmentFamilyText,
    serialNumberText
  ) => {
    return (
      <div>
        <div className="d-flex w-100">
          <p className="u-color--MediumBlue my-1">
            {isSuccess ? factoryFitText : partUnableToVerifyText}
          </p>
          <InfoIcon
            width={16}
            height={16}
            className="mt-1 ms-2 u-fill-icon--ExtraDarkGray"
          />
        </div>
        {!!modelText && (
          <div className={`w-100 u-color--DarkGray ${styles['fitment-small']}`}>
            {getfitmentLabel(modelText, equipmentFamilyText, serialNumberText)}
          </div>
        )}
      </div>
    );
  };

  const getCardFitmentText = serialNumberText => {
    return (
      <div className="d-flex align-items-center w-auto">
        <p className={`u-color--DarkGray mb-0 ${styles['fitment-small']}`}>
          {getfitmentLabel(model, '', serialNumberText, true)}
        </p>
      </div>
    );
  };

  const renderOptions = {
    [FULL_FITMENT]: {
      icon: SuccessIcon,
      buttonTitle: showPopup
        ? getButtonTitle(true, model, equipmentFamily, serialNumber)
        : getCardFitmentText(serialNumber),
      fillClass: 'u-fill-icon--MediumGreen',
      popupContent: [
        {
          text: t('FACTORY_FIT_MESSAGE'),
          className: 'fw-bold'
        },
        {
          text: t('FACTORY_FIT_TEXT')
        }
      ],
      altTagTitle: `${factoryFitText}, ${getfitmentLabel(
        model,
        equipmentFamily,
        serialNumber
      )}`
    },
    [PARTIAL_FITMENT]: {
      icon: WarningIcon,
      buttonTitle: getButtonTitle(false, model, equipmentFamily, ''),
      fillClass: 'u-fill-icon--MediumOrange',
      popupContent: [
        {
          text: t('UNABLE_TO_VERIFY_MESSAGE'),
          className: 'fw-bold'
        },
        {
          text: t('UNABLE_TO_VERIFY_MESSAGE_TEXT')
        }
      ],
      altTagTitle: partUnableToVerifyText
    },
    [NO_FITMENT]: {
      icon: WarningIcon,
      buttonTitle: getButtonTitle(false, model, '', ''),
      fillClass: 'u-fill-icon--MediumOrange',
      popupContent: [
        {
          text: t('UNABLE_TO_VERIFY_MESSAGE'),
          className: 'fw-bold'
        },
        {
          text: t('UNABLE_TO_VERIFY_MESSAGE_TEXT')
        }
      ],
      altTagTitle: partUnableToVerifyText
    }
  };
  const { icon, buttonTitle, fillClass, popupContent, altTagTitle } =
    renderOptions[option] || {};

  const getFitmentContent = () => (
    <FitmentContent
      variant={variant}
      icon={icon}
      iconSize={iconSize}
      className={`${fitmentContentClassName} ${fillClass}`}
      buttonTitle={buttonTitle}
    />
  );

  const getCardFitmentContent = () =>
    fitmentResult === FITMENT_CHECK.FULL_CHECK &&
    !showPopup && (
      <FitmentContent
        variant={variant}
        icon={icon}
        iconSize={iconSize}
        className={`${fitmentContentClassName} ${fillClass}`}
        buttonTitle={buttonTitle}
      />
    );

  const getInitiator = () => (
    <AlloyButton
      buttonType="ghost"
      className={`d-flex flex-row p-0 pe-2 text-start ${styles['glow-on-hover']}`}
      title={altTagTitle}
    >
      {getFitmentContent()}
    </AlloyButton>
  );
  const getPopupContent = () => {
    return (
      Array.isArray(popupContent) &&
      popupContent.map(({ text, className }) => (
        <p className={`text-xs exclude-global ${className}`} key={text}>
          {text}
        </p>
      ))
    );
  };

  const getPopup = () => (
    <Popup
      className={`${styles['fitment-popup']}`}
      contents={getPopupContent()}
      initiator={getInitiator()}
    />
  );

  useEffect(() => {
    if (didFitmentCallResolve) {
      if (fitmentResult === FITMENT_CHECK.FULL_CHECK) {
        setOption(FULL_FITMENT);
      } else if (fitmentResult === FITMENT_CHECK.PARTIAL_CHECK) {
        setOption(PARTIAL_FITMENT);
      } else {
        setOption(NO_FITMENT);
      }
    }
  }, [
    FULL_FITMENT,
    NO_FITMENT,
    PARTIAL_FITMENT,
    fitmentResult,
    didFitmentCallResolve
  ]);

  return (
    <Conditional
      fallback={
        <SkeletonLoader
          data-testid="fitment-skeleton-loader"
          className={cx(
            styles['fitment-skeleton-loader'],
            'w-100',
            fitmentContentClassName
          )}
        />
      }
      test={didFitmentCallResolve}
    >
      {showPopup ? getPopup() : getCardFitmentContent()}
    </Conditional>
  );
};

FitmentResult.propTypes = {
  variant: PropTypes.shape({
    name: PropTypes.string,
    iconSize: PropTypes.number,
    classes: PropTypes.string,
    showPopup: PropTypes.bool
  }).isRequired,
  partNumber: PropTypes.string,
  didFitmentCallResolve: PropTypes.bool,
  getFitmentResult: PropTypes.func
};

export default FitmentResult;
